import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, LogOut, Loader2, Calendar, MessageCircle, Copy, Rocket, Users,
  Gift, Trophy, Bell, ShieldCheck,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import SupportDrawer from "../components/SupportDrawer";
import { paymentStatus, myReferral } from "../lib/api";
import { clearAuth, getUser } from "../lib/auth";

function pad(n) { return String(n).padStart(2, "0"); }

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  return { days, hours, mins, secs, reached: diff === 0 };
}

function PaymentCelebration({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white celebration-enter" data-testid="payment-celebration">
      <div className="relative">
        <span className="celebration-ring celebration-ring-1" />
        <span className="celebration-ring celebration-ring-2" />
        <span className="celebration-ring celebration-ring-3" />
        <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#ff7a1a] to-[#ff4d00] flex items-center justify-center shadow-[0_20px_50px_-12px_rgba(255,90,0,0.55)]">
          <CheckCircle2 className="w-12 h-12 text-white celebration-check" strokeWidth={2.5} />
        </div>
      </div>
      <h2 className="mt-8 font-display font-bold text-[26px] tracking-tight text-black celebration-fade">
        Payment successful
      </h2>
      <p className="mt-2 text-[13.5px] text-neutral-500 celebration-fade-2">Your seat is confirmed</p>
    </div>
  );
}

export default function Success() {
  const nav = useNavigate();
  const user = getUser();
  const [status, setStatus] = useState(null);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supportOpen, setSupportOpen] = useState(false);
  const shownRef = useRef(false);
  const [celebrating, setCelebrating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([paymentStatus(), myReferral().catch(() => null)]);
        if (!s.paid) {
          nav("/payment", { replace: true });
          return;
        }
        setStatus(s);
        setRef(r);
        if (!shownRef.current) {
          shownRef.current = true;
          setCelebrating(true);
        }
      } catch {
        clearAuth();
        nav("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const unlockIso = status?.access_unlock_iso || "2026-08-10T00:00:00+05:30";
  const cd = useCountdown(unlockIso);
  const logout = () => { clearAuth(); nav("/login", { replace: true }); };

  const referralUrl = ref?.referral_code
    ? `${window.location.origin}/signup?ref=${ref.referral_code}`
    : "";

  const copyRef = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const shareRef = async () => {
    if (!referralUrl) return;
    const msg = `I just pre-booked GGES Ventures — join with my code ${ref.referral_code} and we both win.\n${referralUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: "GGES Ventures", text: msg, url: referralUrl }); return; } catch {}
    }
    copyRef(msg, "Share text copied");
  };

  if (loading || !status) {
    return (
      <AppShell>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {celebrating && <PaymentCelebration onDone={() => setCelebrating(false)} />}
      <SupportDrawer open={supportOpen} onClose={() => setSupportOpen(false)} />

      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex items-center justify-between px-6 pt-5">
          <Logo size="sm" />
          <button
            onClick={logout}
            data-testid="success-logout-btn"
            className="h-9 px-3 rounded-full border border-neutral-200 flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>

        <div className="screen screen--with-cta flex-1">
          {/* Header */}
          <div className="mt-4 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center ring-4 ring-emerald-50">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
            </div>
            <h1 className="mt-5 font-display font-bold text-[26px] tracking-tight" data-testid="success-title">
              You're in
            </h1>
            <p className="mt-1.5 text-[13px] text-neutral-500 max-w-xs">
              {user?.name ? `${user.name.split(" ")[0]}, w` : "W"}elcome to GGES Ventures.
            </p>
          </div>

          {/* Countdown card */}
          <div className="mt-8 rounded-3xl bg-gradient-to-b from-[#0A0A0A] to-[#1a1a1a] text-white p-6 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand/20 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-brand/20 text-brand text-[10px] font-semibold tracking-widest uppercase">
                  <Rocket className="w-3 h-3" />
                  Launch day
                </div>
                <span className="text-[11px] text-neutral-400 tracking-wide">10 Aug 2026 · IST</span>
              </div>
              <div className="mt-5 text-[12px] uppercase tracking-[0.18em] text-neutral-400">Access unlocks in</div>
              <div className="mt-3 grid grid-cols-4 gap-2" data-testid="countdown">
                <Cell num={cd.days} lbl="Days" testId="cd-days" />
                <Cell num={pad(cd.hours)} lbl="Hours" testId="cd-hours" />
                <Cell num={pad(cd.mins)} lbl="Mins" testId="cd-mins" />
                <Cell num={pad(cd.secs)} lbl="Secs" testId="cd-secs" />
              </div>
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Amount paid</div>
                  <div className="font-display font-bold text-[22px] tabular-nums mt-1" data-testid="amount-paid">
                    ₹{Number(status.amount_paid || 11883).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">You saved</div>
                  <div className="font-display font-bold text-[22px] tabular-nums mt-1 text-brand">
                    {status.discount_percent}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refer & Earn CTA */}
          {ref && (
            <div className="mt-8 relative rounded-3xl border-2 border-brand/40 bg-gradient-to-br from-white via-[#FFF6EE] to-[#FFE4CC] p-5 overflow-hidden" data-testid="referral-card">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-brand/15 blur-2xl" />
              <div className="relative flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-brand text-white text-[10px] font-bold tracking-widest">
                  <Gift className="w-3 h-3" /> REFER & EARN 25%
                </div>
                <div className="text-right">
                  <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 font-semibold">Earned</div>
                  <div className="font-display font-bold text-[18px] tabular-nums" data-testid="referral-earnings">
                    ₹{Number(ref.referral_earnings_inr || 0).toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              <div className="relative mt-4 text-[13px] text-neutral-700 leading-relaxed">
                Earn <span className="font-semibold text-black">₹{ref.earning_per_signup_inr.toLocaleString("en-IN")}</span> for every friend who pre-books using your code — and they get 10% off too.
              </div>

              <div className="relative mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="p-2 rounded-xl bg-white border border-neutral-200">
                  <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 font-bold">Referrals</div>
                  <div className="font-display font-bold text-[16px] tabular-nums mt-0.5" data-testid="referral-count">{ref.referral_count || 0}</div>
                </div>
                <div className="p-2 rounded-xl bg-white border border-neutral-200">
                  <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 font-bold">Your code</div>
                  <div className="font-mono font-bold text-[14px] tracking-widest mt-0.5" data-testid="referral-code">{ref.referral_code}</div>
                </div>
              </div>

              <button
                onClick={() => nav("/refer-earn")}
                data-testid="open-refer-earn-btn"
                className="btn-brand w-full h-11 mt-4 flex items-center justify-center gap-2 text-[13.5px]"
              >
                Refer & Earn — Withdraw
              </button>
            </div>
          )}

          {/* Unlock preview */}
          <div className="mt-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
              What unlocks on launch day
            </div>
            <ul className="mt-3 space-y-2.5">
              {[
                "Full short video learning library",
                "Structured courses & education vault",
                "Weekly crypto & investment predictions",
                "Private members-only community",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[13.5px] text-neutral-700">
                  <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Reminder + security */}
          <div className="mt-6 p-4 rounded-2xl bg-neutral-50 border border-neutral-200 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-brand" />
            </div>
            <div className="text-[13px] text-neutral-700 leading-relaxed">
              We'll send you a reminder on <span className="font-semibold">{user?.phone}</span> just before launch day.
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 text-[11px] text-neutral-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            Payment secured by Razorpay · Receipt sent to your account
          </div>

          <div className="mt-6 flex justify-center">
            <button
              disabled={!cd.reached}
              data-testid="enter-platform-btn"
              className={`h-11 px-6 rounded-full font-semibold flex items-center justify-center transition-colors text-[14px] min-w-[220px] ${
                cd.reached ? "btn-brand" : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
              }`}
            >
              {cd.reached ? "Enter platform" : "Locked until launch"}
            </button>
          </div>
        </div>

        {/* Fixed bottom Chat Support */}
        <div className="sticky-cta">
          <button
            onClick={() => setSupportOpen(true)}
            data-testid="open-support-btn"
            className="w-full h-11 rounded-full bg-black text-white font-semibold flex items-center justify-center gap-2 text-[14px] hover:bg-neutral-800 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Chat support
          </button>
          <div className="mt-1.5 text-center text-[10.5px] text-neutral-500 tabular-nums">
            FAQs · Payment issues · Raise a ticket
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Cell({ num, lbl, testId }) {
  return (
    <div className="rounded-xl bg-white/[0.06] border border-white/10 p-3 text-center" data-testid={testId}>
      <div className="font-display font-bold text-[24px] tabular-nums leading-none">{num}</div>
      <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-400 mt-1.5">{lbl}</div>
    </div>
  );
}
